import '@main/complykit/theme/complykit.built.css';
import './datadog-logger';
import './utils/i18n';

import { ChakraProvider, useColorMode } from '@chakra-ui/react';
import { ThemeProvider as ComplykitThemeProvider } from '@main/complykit';
import { AssetUrlProvider } from '@main/shared/browser';
import { AlertDialogContainer, theme, ToastContainer, useAlertDialog } from '@main/ui';
import { NhostProvider } from '@nhost/react';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import { router } from './router';
import { store } from './store';
import { nhost } from './utils/nhost-client';

const AppRouter = () => {
  const dialog = useAlertDialog();

  return <RouterProvider router={router} context={{ dialog }} />;
};

/**
 * Chakra's color mode is the app's appearance source of truth (it owns the
 * toggle and persistence); ComplyKit follows it. The bridge forces the
 * ComplyKit theme (the .dark class driving every token variable) from
 * Chakra's current mode, so both systems always switch together.
 */
const ComplykitColorModeBridge = ({ children }: { children: React.ReactNode }) => {
  const { colorMode } = useColorMode();

  return (
    <ComplykitThemeProvider forcedTheme={colorMode === 'dark' ? 'dark' : 'light'}>
      {children}
    </ComplykitThemeProvider>
  );
};

const App = () => {
  return (
    <StrictMode>
      <AssetUrlProvider value={import.meta.env.VITE_ASSETS_BASE_URL}>
        <Provider store={store}>
          <ChakraProvider theme={theme} portalZIndex={40}>
            <ComplykitColorModeBridge>
              <NhostProvider nhost={nhost}>
                <AlertDialogContainer>
                  <AppRouter />
                </AlertDialogContainer>
              </NhostProvider>
            </ComplykitColorModeBridge>
          </ChakraProvider>
          <ToastContainer />
        </Provider>
      </AssetUrlProvider>
    </StrictMode>
  );
};

const rootElement = document.getElementById('root') as HTMLElement;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
